import React, { useCallback, useEffect, useState } from "react";
import poster from "./poster.jpg";
import Modal from "@mui/material/Modal";
import { fetchJson } from './api';

import { Box, Typography, Button, CircularProgress, Alert, Paper, Stack, TextField, Container } from "@mui/material";

// Demo movie info (replace with props or API as needed)
const MOVIE_INFO = {
  title: "Spider-Man: Brand New Day",
  poster: poster, // Use imported poster image
  showtime: "Wednesday, July 22, 2026, 7:30 PM",
  theater: "Cinemark Centreville",
  auditorium: "Auditorium 1",
  address: "6201 Multiplex Dr, Centreville, VA 20121",
  synopsis: "Peter Parker faces a new beginning as Spider-Man, balancing heroics and everyday life.",
  rating: "PG-13",
  runtime: "2h 10m",
  genre: "Action/Adventure"
};

// Helper to generate seat layout (skip row I)
const generateSeats = () => {
  const rows = [];
  // Row A: 8 seats
  rows.push({ row: 'A', seats: Array.from({ length: 8 }, (_, i) => `A${i + 1}`) });
  // Rows B-K (excluding I): 19 seats each
  const rowLetters = [];
  for (let code = 66; code <= 75; code++) { // B-K
    const letter = String.fromCharCode(code);
    if (letter !== 'I') {
      rowLetters.push(letter);
    }
  }
  for (const rowLetter of rowLetters) {
    rows.push({ row: rowLetter, seats: Array.from({ length: 19 }, (_, i) => `${rowLetter}${i + 1}`) });
  }
  return rows;
};

const seatLayout = generateSeats();

function TheaterSeatPicker() {
  const [seats, setSeats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reserveMsg, setReserveMsg] = useState("");
  const [confirmedSeats, setConfirmedSeats] = useState([]);
  const [emailWarning, setEmailWarning] = useState("");
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [showContact, setShowContact] = useState(false);
  const [contact, setContact] = useState({ name: '', phone: '', email: '' });
  const [contactError, setContactError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // disables submit button

  // Move fetchSeats outside useEffect so it can be called after reservation
  const fetchSeats = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchJson('/seats');
      if (!Array.isArray(data.seats) || !data.seats.length) {
        throw new Error('Could not load seat data.');
      }
      const seatMap = {};
      data.seats.flat().forEach(s => {
        if (typeof s?.seat !== 'string' || typeof s.reserved !== 'boolean') {
          throw new Error('Could not load seat data.');
        }
        seatMap[s.seat] = s.reserved;
      });
      setSeats(seatMap);
    } catch (err) {
      setError(err.message || "Could not load seat data.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSeats();
  }, [fetchSeats]);

  const handleSeatClick = (seat) => {
    if (loading || seats[seat] !== false) return;
    setSelectedSeats(prev => prev.includes(seat) ? prev.filter(s => s !== seat) : [...prev, seat]);
  };

  const handleConfirm = () => {
    if (selectedSeats.length === 0 || isSubmitting) return;
    setContactError("");
    setShowContact(true);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // Prevent double submit
    setIsSubmitting(true);
    setContactError("");
    if (!contact.name.trim() || !contact.phone.trim() || !contact.email.trim()) {
      setContactError("Please enter your name, phone number, and email.");
      setIsSubmitting(false);
      return;
    }
    // Basic email validation
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contact.email.trim())) {
      setContactError("Please enter a valid email address.");
      setIsSubmitting(false);
      return;
    }
    try {
      const data = await fetchJson('/reserve-seats', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          seats: selectedSeats, 
          name: contact.name.trim(),
          phone: contact.phone.trim(),
          email: contact.email.trim()
        })
      });
      if (!Array.isArray(data.reserved) || !Array.isArray(data.failed)) {
        throw new Error('The server did not return a valid booking result. Check availability before retrying.');
      }
      const { reserved, failed } = data;
      setConfirmedSeats(reserved);
      setEmailWarning(data.email_status === 'failed'
        ? data.email_error || 'Your seats are reserved, but the confirmation email could not be sent.'
        : '');
      setReserveMsg(
        reserved.length && !failed.length
          ? `Reserved seats: ${reserved.join(", ")}`
          : failed.length && reserved.length
            ? `Some seats could not be reserved: ${failed.join(", ")}. Reserved: ${reserved.join(", ")}`
            : "No seats could be reserved. Please select available seats."
      );
      setSelectedSeats([]);
      setShowContact(false);
      setShowModal(reserved.length > 0);
      await fetchSeats();
    } catch (err) {
      setContactError(err.message || 'Could not complete the reservation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading && !Object.keys(seats).length) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px"><CircularProgress /></Box>;
  if (error && !showModal) return <Alert severity="error" action={<Button onClick={fetchSeats}>Retry</Button>}>{error}</Alert>;

  // Modal content for reservation confirmation (restored: not full screen, styled box)
  const ConfirmationModal = (
    <Modal
      open={showModal}
      onClose={() => setShowModal(false)}
      aria-labelledby="reservation-success-modal"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}
    >
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby="reservation-success-modal"
        sx={{
          bgcolor: 'var(--white)',
          border: '4px solid var(--crimson)',
          borderRadius: 6,
          boxShadow: '0 0 32px 8px rgba(214, 40, 57, 0.3)',
          p: { xs: 2, sm: 4 },
          width: { xs: 'calc(100% - 32px)', sm: 480 },
          minWidth: 0,
          maxWidth: 480,
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          outline: 'none',
          textAlign: 'center',
          position: 'relative',
        }}
      >
        <img
          src="/static/media/Spider-Man_thumbs_up.png"
          alt="Spider-Man thumbs up"
          style={{
            display: 'block',
            borderRadius: 12,
            boxShadow: '0 2px 12px rgba(75, 163, 195, 0.5)',
            marginLeft: 'auto',
            marginRight: 'auto',
            marginBottom: 16,
            maxWidth: '100%',
            maxHeight: '300px',
            width: 'auto',
            height: 'auto'
          }}
        />
        <Typography id="reservation-success-modal" variant="h4" sx={{ color: 'var(--crimson)', fontWeight: 900, mb: 1, letterSpacing: 1 }}>
          See You Soon!
        </Typography>
        <Typography variant="h6" sx={{ color: 'var(--chinese-violet)', fontWeight: 700, mb: 2 }}>
          Your seats are confirmed!
        </Typography>
        <Typography variant="body1" sx={{ color: 'var(--black)', fontWeight: 500, mb: 1 }}>
          <strong>Movie:</strong> {MOVIE_INFO.title}
        </Typography>
        <Typography variant="body1" sx={{ color: 'var(--black)', fontWeight: 500, mb: 1 }}>
          <strong>Showtime:</strong> {MOVIE_INFO.showtime}
        </Typography>
        <Typography variant="body1" sx={{ color: 'var(--black)', fontWeight: 500, mb: 1 }}>
          <strong>Theater:</strong> {MOVIE_INFO.theater}
        </Typography>
        <Typography variant="body1" sx={{ color: 'var(--black)', fontWeight: 500, mb: 2 }}>
          <strong>Seats:</strong> {confirmedSeats.join(', ')}
        </Typography>
        {emailWarning && <Alert severity="warning" sx={{ mb: 2 }}>{emailWarning}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        <Button
          variant="contained"
          size="large"
          sx={{ 
            borderRadius: 4, 
            fontWeight: 700, 
            mt: 1, 
            px: 4, 
            fontSize: 18, 
            background: 'var(--crimson)',
            boxShadow: '0 2px 8px rgba(214, 40, 57, 0.4)',
            '&:hover': { background: 'var(--rose-red)' }
          }}
          onClick={() => setShowModal(false)}
        >
          Close
        </Button>
      </Box>
    </Modal>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4, backgroundColor: 'var(--background-color)', color: 'var(--text-color)' }}>
      {ConfirmationModal}
      {reserveMsg && <Alert severity={confirmedSeats.length ? 'info' : 'warning'} sx={{ mb: 2 }}>{reserveMsg}</Alert>}
      <Paper elevation={12} sx={{ p: { xs: 2, md: 4 }, bgcolor: 'var(--card-background)', borderRadius: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
          <Box
            component="img"
            src={MOVIE_INFO.poster}
            alt={MOVIE_INFO.title}
            sx={{
              width: { xs: '100%', md: 250 },
              height: 'auto',
              borderRadius: 2,
              boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              objectFit: 'cover'
            }}
          />
          <Box>
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'var(--crimson)' }}>
              {MOVIE_INFO.title}
            </Typography>
            <Typography variant="h5" gutterBottom>{MOVIE_INFO.showtime}</Typography>
            <Typography variant="h6" gutterBottom>{MOVIE_INFO.theater} - {MOVIE_INFO.auditorium}</Typography>
            <Typography variant="body1" gutterBottom sx={{ fontStyle: 'italic' }}>{MOVIE_INFO.address}</Typography>
            <Stack direction="row" spacing={2} sx={{ my: 2 }}>
              <Button variant="outlined" size="small" sx={{ borderColor: 'var(--text-color)', color: 'var(--text-color)' }}>{MOVIE_INFO.rating}</Button>
              <Button variant="outlined" size="small" sx={{ borderColor: 'var(--text-color)', color: 'var(--text-color)' }}>{MOVIE_INFO.runtime}</Button>
              <Button variant="outlined" size="small" sx={{ borderColor: 'var(--text-color)', color: 'var(--text-color)' }}>{MOVIE_INFO.genre}</Button>
            </Stack>
            <Typography variant="body1" sx={{ mt: 2 }}>{MOVIE_INFO.synopsis}</Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 'bold' }}>Select Your Seats</Typography>
          
          {/* Screen */}
          <Box sx={{ 
            width: '80%', 
            height: '8px', 
            background: 'linear-gradient(90deg, transparent, var(--pacific-cyan), transparent)', 
            mx: 'auto', 
            mb: 1,
            borderRadius: '50%'
          }} />
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'rgba(0,0,0,0.4)', mb: 6, letterSpacing: 2 }}>
            SCREEN
          </Typography>

          {/* Seats */}
          <Box sx={{ width: '100%', pb: 2, display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.5, sm: 1, md: 1.5 }, alignItems: 'center', width: '100%', maxWidth: '800px' }}>
              {seatLayout.map((row, i) => (
                <Box key={row.row} sx={{ display: 'flex', gap: { xs: 0.25, sm: 0.5, md: 1 }, width: '100%' }}>
                  <Typography sx={{ flex: { xs: '0 0 12px', sm: '0 0 16px', md: '0 0 24px' }, fontSize: { xs: '0.5rem', sm: '0.8rem', md: '1rem' }, color: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center' }}>
                    {row.row}
                  </Typography>
                  {row.seats.map((seatId) => {
                    const isReserved = seats[seatId] !== false;
                    const isSelected = selectedSeats.includes(seatId);
                    return (
                      <Button
                        key={seatId}
                        variant="contained"
                        aria-label={`Seat ${seatId}`}
                        aria-pressed={isSelected}
                        onClick={() => handleSeatClick(seatId)}
                        disabled={isReserved || loading || !!error}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          height: { xs: '18px', sm: '26px', md: '32px' },
                          fontSize: { xs: '0.35rem', sm: '0.65rem', md: '0.75rem' },
                        p: 0,
                        bgcolor: isSelected
                          ? 'primary.main'
                          : isReserved
                            ? 'grey.700' // Darker grey for reserved
                            : 'grey.200', // Lighter grey for available
                        color: isSelected || isReserved ? 'white' : 'black',
                        '&:hover': {
                          bgcolor: isSelected ? 'primary.dark' : !isReserved ? 'grey.400' : 'grey.700',
                        },
                        '&.Mui-disabled': {
                          bgcolor: 'grey.700',
                          color: 'white',
                        },
                      }}
                    >
                      {seatId.substring(1)}
                    </Button>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>

          {/* Legend */}
          <Stack direction="row" spacing={4} justifyContent="center" sx={{ mt: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 16, height: 16, borderRadius: '4px', bgcolor: 'grey.200', border: '1px solid rgba(0,0,0,0.2)' }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Available</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 16, height: 16, borderRadius: '4px', bgcolor: 'primary.main' }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Selected</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 16, height: 16, borderRadius: '4px', bgcolor: 'grey.700' }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Reserved</Typography>
            </Box>
          </Stack>

          {/* Action Bar */}
          <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Button
              variant="contained"
              size="large"
              disabled={selectedSeats.length === 0 || isSubmitting || loading}
              onClick={handleConfirm}
              sx={{
                background: 'var(--crimson)',
                px: 6,
                py: 1.5,
                borderRadius: 3,
                fontWeight: 700,
                '&:hover': { background: 'var(--rose-red)' },
                '&.Mui-disabled': { background: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.26)' }
              }}
            >
              Confirm Seats
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Contact Modal */}
      <Modal open={showContact} onClose={() => { if (!isSubmitting) setShowContact(false); }}>
        <Box role="dialog" aria-modal="true" aria-labelledby="contact-dialog-title" sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          bgcolor: 'var(--white)',
          borderRadius: 4,
          boxShadow: 24,
          p: 4,
        }}>
          <Typography id="contact-dialog-title" variant="h5" sx={{ color: 'var(--rosewood)', mb: 3, fontWeight: 700 }}>
            Complete Reservation
          </Typography>
          <form onSubmit={handleContactSubmit}>
            <Stack spacing={3}>
              {contactError && <Alert severity="error">{contactError}</Alert>}
              <TextField
                label="Name"
                fullWidth
                required
                value={contact.name}
                onChange={e => setContact({ ...contact, name: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                label="Email"
                type="email"
                fullWidth
                required
                value={contact.email}
                onChange={e => setContact({ ...contact, email: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                label="Phone"
                type="tel"
                fullWidth
                required
                value={contact.phone}
                onChange={e => setContact({ ...contact, phone: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isSubmitting}
                sx={{
                  mt: 2,
                  background: 'var(--crimson)',
                  borderRadius: 2,
                  py: 1.5,
                  fontWeight: 700,
                  '&:hover': { background: 'var(--rose-red)' }
                }}
              >
                {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Book Now"}
              </Button>
            </Stack>
          </form>
        </Box>
      </Modal>
    </Container>
  );
}

export default TheaterSeatPicker;
